// Core packages
import { SelectionModel } from '@angular/cdk/collections';
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";

// Third party packages
import {
  MatDialog, MatPaginator, MatSort
} from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  startWith,
  take,
  tap
} from "rxjs/operators";

// Core packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { AssignTaggersDialogComponent } from 'src/app/modules/properties/components/assign-taggers-dialog/assign-taggers-dialog.component';
import { PropertyTagNfcListDialogComponent } from 'src/app/modules/properties/components/property-tag-nfc-list-dialog/property-tag-nfc-list-dialog.component';
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { Property } from 'src/app/shared/models/property/property.model';
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { PropertiesDataSource } from "src/app/shared/utils/data-sources/properties-data-source";

@Component({
  selector: "app-properties-nfc-list",
  templateUrl: "./properties-nfc-list.page.html",
  styleUrls: ["./properties-nfc-list.page.scss"],
})
export class PropertiesNfcListComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  // BEGIN - Table properties
  displayedColumns: string[] = [
    "index",
    "checkbox",
    "name",
    "admin",
    "address",
    "city",
    "postalCode",
    "nfcTags",
    "user",
  ];
  dataSource: PropertiesDataSource;
  @ViewChild("sort1", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  nameFilter: FormControl = new FormControl("");
  adminFilter: FormControl = new FormControl("");
  addressFilter: FormControl = new FormControl("");
  cityFilter: FormControl = new FormControl("");
  postalCodeFilter: FormControl = new FormControl("");
  nfcTagsFilter: FormControl = new FormControl("");
  userFilter: FormControl = new FormControl("");
  // END - Table properties

  activeRoute: string;
  loggedUserRoleType = "";
  loggedUserAdmin = false;

  availableTaggerUsers: User[] = [];
  taggerUsersIsLoading = false;

  adminIsLoading = false;
  availableAdmins: User[] = [];

  selection = new SelectionModel<Property>(true, []);

  /**
   * Class constructor
   */
  constructor(
    private propertiesService: PropertiesService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private apiService: ApiService,
    private configService: ConfigService,
    private companiesService: CompaniesService,
    public dialog: MatDialog,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.list.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );
    this.authService.loggedUserRequestedRoleType$.subscribe((res) => {
      if (res) {
        this.loggedUserRoleType = res;
        if (
          this.loggedUserRoleType === "admin" ||
          this.loggedUserRoleType === "operator"
        ) {
          this.loggedUserAdmin = true;
        } else {

        }
      }
    });

    // Init data source
    this.dataSource = new PropertiesDataSource(
      this.propertiesService,
      this.helperService
    );

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings("properties");

    // Get tagger users
    this.getTaggerUsers();

    this.getAvailableAdmins();
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            this.loadPage();
          })
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.adminFilter.valueChanges.pipe(startWith(null as string)),
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.addressFilter.valueChanges.pipe(startWith(null as string)),
        this.cityFilter.valueChanges.pipe(startWith(null as string)),
        this.postalCodeFilter.valueChanges.pipe(startWith(null as string)),
        this.nfcTagsFilter.valueChanges.pipe(startWith(null as string)),
        this.userFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe((res) => {
          // Load page only if at least one value is not null
          if (!res.every((value) => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        })
    );

  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    // Push current list settings to the listsService
    if (this.paginator) {
      this.listsService.setListSetting(
        "properties",
        this.paginator.pageIndex.valueOf(),
        this.paginator.pageSize.valueOf(),
        {
          general: "", // this.input.nativeElement.value,
          admin: this.adminFilter.value,
          name: this.nameFilter.value,
          address: this.addressFilter.value,
          city: this.cityFilter.value,
          postalCode: this.postalCodeFilter.value,
          nfcTags: this.nfcTagsFilter.value,
          user: this.userFilter.value,
        }
      );
    }
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: "", // this.input.nativeElement.value,
      name: this.nameFilter.value,
      admin: this.adminFilter.value,
      address: this.addressFilter.value,
      city: this.cityFilter.value,
      postalCode: this.postalCodeFilter.value,
      nfcTags: this.nfcTagsFilter.value,
      user: this.userFilter.value,
    };
    this.dataSource.loadTagNfcItems(
      start,
      length,
      this.sort.active,
      this.sort.direction,
      search
    );
  }

  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    // If target is not provided, than just export all properties
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "properties.list.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.propertiesService.getTagNfcExport().subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = "";
    this.nameFilter.setValue(null);
    this.adminFilter.setValue(null);
    this.addressFilter.setValue(null);
    this.cityFilter.setValue(null);
    this.postalCodeFilter.setValue(null);
    this.nfcTagsFilter.setValue(null);
    this.userFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "asc";
    this.sort.active = "name";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.paginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.paginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                // this.input.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.name) {
                this.nameFilter.setValue(setting.search.name);
              }
              if (setting.search && setting.search.admin) {
                this.adminFilter.setValue(setting.search.admin);
              }
              if (setting.search && setting.search.address) {
                this.addressFilter.setValue(setting.search.address);
              }
              if (setting.search && setting.search.city) {
                this.cityFilter.setValue(setting.search.city);
              }
              if (setting.search && setting.search.postalCode) {
                this.postalCodeFilter.setValue(setting.search.postalCode);
              }
              if (setting.search && setting.search.nfcTags) {
                this.nfcTagsFilter.setValue(setting.search.nfcTags);
              }
              if (setting.search && setting.search.user) {
                this.userFilter.setValue(setting.search.user);
              }
            }
          });
        }
      })
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Assign tagger user
   * 
   * @since 1.0.0
   */
  onAssignTaggers(): void {
    const selectedProperties: string[] = [];
    if (this.selection.selected && Array.isArray(this.selection.selected)) {
      this.selection.selected.forEach((item: any) => {
        selectedProperties.push(item._id);
      })
    }
    const dialogRef = this.dialog.open(AssignTaggersDialogComponent, {
      width: "700",
      data: {
        properties: selectedProperties,
      },
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.selection.clear();
        this.loadPage();
        return;
      });
  }

  /**
   * View the list of installed tags
   * 
   * @since 1.0.0
   */
  onViewDetail(element: any): void {
    this.dialog.open(PropertyTagNfcListDialogComponent, {
      width: "80%",
      data: {
        propertyId: element._id,
      },
    });
  }
  
  
  /**
   * Get tagger users
   */
  getTaggerUsers(search?: any): void {
    this.taggerUsersIsLoading = true;

    this.subscriptions.push(
      this.propertiesService
        .getTaggerUsers(0, 10, null, null, search)
        .pipe(
          finalize(() => {
            this.taggerUsersIsLoading = false;
          })
        )
        .subscribe((res) => {
          if (res.status) {
            this.availableTaggerUsers = res.data.items;
          }
        })
    )
  }

  /** 
   * Whether the number of selected elements matches the total number of rows. 
   */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.itemsLength;

    return numSelected === numRows;
  }

  /** 
   * Selects all rows if they are not all selected; otherwise clear selection. 
   */
  masterToggle(): void {
    this.isAllSelected() ?
        this.selection.clear() :
        this.dataSource.items.forEach(row => this.selection.select(row));

        console.log(this.selection)
  }
  
  /**
   * Get the list of available admins
   * used to allow filtering on admin columns
   *
   * @since 1.0.0
   */
  getAvailableAdmins(search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, 10, null, null, search)
        .pipe(
          finalize(() => {
            this.adminIsLoading = false;
          })
        )
        .subscribe((res) => {
          if (res.status) {
            this.availableAdmins = res.data.items;
          }
        })
    );
  }
}
